/**
 * The transaction review's VALUE CONTRACT, kept in a JSX-free module — Phase 06.
 *
 * It lives apart from `TransactionReview.tsx` for one reason: the row builders
 * that feed the panel are pure data functions, and this way they — and their
 * tests — can be imported without pulling a React component tree in. The
 * component re-exports everything here, so consumers still import from one
 * place.
 *
 * A row's value is one of four kinds, and the type makes the dishonest options
 * unrepresentable — there is no "just a number" variant and no default:
 *
 *   value        a real figure with a real source
 *   estimate     a real figure that is NOT a quote — `basis` is required
 *   pending      it will exist, but not yet
 *   unavailable  it cannot be produced — `reason` names the dependency
 *
 * THE QUOTE RULE (Phase 06 §12.2, which Phase 08's swap work inherits). A real
 * QUOTE requires verified quote data, an expiry and a slippage figure, all
 * three. Without them the surface has an ESTIMATE and must say so — which is
 * why `estimate` carries a required `basis` naming what the number is and what
 * it excludes. Confirming against a figure labelled "before fees" is the defect
 * the rule exists to stop; there is no code path here that renders an estimate
 * as if it were a quote.
 */

export type ReviewValue =
  | { kind: "value"; text: string; technical?: boolean; note?: string }
  /** A figure that is not a quote. `basis` states what it is and what it excludes. */
  | { kind: "estimate"; text: string; basis: string; technical?: boolean }
  /** Not yet available. `reason` says what it is waiting for. */
  | { kind: "pending"; reason: string }
  /** Cannot be produced at all. `reason` names the missing dependency. */
  | { kind: "unavailable"; reason: string };

export type ReviewRow = {
  /** Stable key. Also the anchor a test or a report refers to. */
  id: string;
  label: string;
  value: ReviewValue;
  /** `total` and `net` are the two rows a user reads last and hardest. */
  emphasis?: "default" | "total" | "net";
};

export const sourced = (text: string, note?: string): ReviewValue => ({ kind: "value", text, note });
export const estimate = (text: string, basis: string): ReviewValue => ({ kind: "estimate", text, basis });
export const pending = (reason: string): ReviewValue => ({ kind: "pending", reason });
export const unavailable = (reason: string): ReviewValue => ({ kind: "unavailable", reason });
